import type { ListArticleData } from '../types/editorial-types';

import { computed } from 'vue';

import { storeToRefs } from 'pinia';
import { useRoute } from 'vue-router/composables';

import { useQuery } from '@/helpers/composables/useApollo';
import { captureMessageForSentry } from '@/helpers/sentry-helper';

import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useMetaStore } from '@/stores/pinia/useMetaStore';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';

import { GetArticleDocument, GetArticleQuery } from '@/features/editorial/graphql/queries/GetArticle.query';
import { BackdropProfile, ImageFormat } from '@/@types/graphql-types';

export function useEditorialArticle() {
	const route = useRoute();

	const { language, country } = storeToRefs(useLanguageStore());
	const { activeDomain } = storeToRefs(useRoutingStore());
	const { setArticleJsonLD } = useMetaStore();

	const queryVariables = computed(() => {
		return {
			withOffers: true,
			fullPath: route.path,
			site: activeDomain.value,
			country: country.value,
			language: language.value,
			first: 10,
			profile: BackdropProfile.S1440,
			format: ImageFormat.Webp,
			filter: {
				ageCertifications: [],
				excludeGenres: [],
				excludeProductionCountries: [],
				objectTypes: [],
				productionCountries: [],
				subgenres: [],
				genres: [],
				packages: [],
				excludeIrrelevantTitles: false,
				presentationTypes: [],
				monetizationTypes: [],
				includeTitlesWithoutUrl: true,
			},
			authorPreview: true,
		};
	});

	const enabled = computed(() => route.name === 'app.editorial.subdomain.article');

	const {
		result,
		fetchMore,
		loading: isLoadingArticles,
		onError,
		onResult,
	} = useQuery<GetArticleQuery>(GetArticleDocument, queryVariables, { errorPolicy: 'all', enabled });

	onError(error => {
		captureMessageForSentry(
			'[GraphQL GetArticle]:',
			{ error: error, where: 'Composable: useEditorialArticle.ts' },
			'error'
		);
	});

	onResult(({ data }) => {
		if (data?.urlV2.node != null) setArticleJsonLD(data.urlV2.node as ListArticleData, route.fullPath);
	});

	const article = computed(() => result.value?.urlV2.node as ListArticleData | undefined);

	const isLegacy = computed(() => article.value?.content.isLegacy ?? null);

	return {
		article,
		isLegacy,
		isLoadingArticles,
		fetchMore,
	};
}
